import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import type { Comment } from '../../../shared/types.ts';
import type { AuthUser } from '../hooks/useApp.ts';

interface Props {
  slug: string;
  user: AuthUser | null;
  onRequestSignIn: () => void;
}

export function Comments({ slug, user, onRequestSignIn }: Props): JSX.Element {
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listComments(slug).then(d => setComments(d.comments));
  }, [slug]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const { comment } = await api.postComment(slug, body);
      setComments(prev => [comment, ...prev]);
      setBody('');
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number): Promise<void> => {
    if (!confirm('Delete this comment?')) return;
    try {
      await api.deleteComment(id);
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'failed');
    }
  };

  return (
    <section className="comments">
      <h2>
        Discussion <span className="count">{comments.length}</span>
      </h2>
      {user ? (
        <form className="comment-form" onSubmit={submit}>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Share your take, ask a question, or correct me…"
            maxLength={2000}
            required
          />
          <div className="row">
            <span className="hint">Posting as <strong>@{user.username}</strong> · markdown not rendered</span>
            <button className="primary" type="submit" disabled={busy || !body.trim()}>
              {busy ? '…' : 'Post'}
            </button>
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        </form>
      ) : (
        <div className="signed-out-cta">
          Join the conversation — sign in to comment.
          <br />
          <button className="primary" onClick={onRequestSignIn}>Sign in or create an account</button>
        </div>
      )}

      <div className="comment-list">
        {comments.map(c => (
          <div className="comment" key={c.id}>
            <div className="head">
              <span className="who">@{c.username}</span>
              <span>{formatDate(c.created_at)}</span>
              {user && user.username === c.username && (
                <button className="delete-btn" onClick={() => remove(c.id)}>delete</button>
              )}
            </div>
            <div className="body">{c.body}</div>
          </div>
        ))}
        {comments.length === 0 && (
          <div style={{ color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>
            No comments yet — be the first.
          </div>
        )}
      </div>
    </section>
  );
}

function formatDate(iso: string): string {
  // SQLite emits "YYYY-MM-DD HH:MM:SS" in UTC; tack on a Z so JS parses it.
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
